/**
 * The log on a phone: one card per entry, and a sheet to change one.
 *
 * The desk's log is seventeen columns of AG Grid, about 3,300 pixels wide, edited by
 * double-click into popup editors — none of which survives a 390-pixel screen and a thumb. So a
 * phone gets the same rows as cards: what it is, who it is for, what she wrote, and what is
 * still to do. Tapping Edit opens the fields she actually changes away from a desk (summary,
 * key points, clients, tags, Norwood action, board flag, status) and saves them in **one**
 * PATCH, the same call the grid makes one cell at a time.
 *
 * The clients are chips driven by `toggleClient`, the function the review queue and the grid's
 * picker share, so "All" means the same thing here as there. Next steps open `ActionsPanel`,
 * exactly as the grid's cell does: an item's actions are separate records, not a field.
 */

import { useEffect, useMemo, useState } from "react";

import { formatDate } from "../config";
import type { Client, Item, ItemPatch } from "../types";
import {
  ALL_CLIENTS_TAG,
  LOG_STATUSES,
  NORWOOD_ACTIONS,
  clientLabel,
  clientTags,
  coversLive,
  toggleClient,
} from "../types";
import { ItemLink } from "./ItemLink";

/** Cards rendered at once. A few hundred DOM cards is slow on a phone; more is a tap away. */
const PAGE = 40;

interface CardsProps {
  items: Item[];
  live: string[];
  onEdit: (item: Item) => void;
  onActions: (item: Item) => void;
}

export function LogCards({ items, live, onEdit, onActions }: CardsProps) {
  const [shown, setShown] = useState(PAGE);
  // A new filter is a new list; start it from the top rather than from wherever the last one
  // had been expanded to.
  useEffect(() => setShown(PAGE), [items]);

  if (!items.length) return <p className="muted empty">Nothing in the log matches.</p>;

  return (
    <>
      <ul className="log-cards">
        {items.slice(0, shown).map((item) => {
          const clients = item.clients?.length
            ? clientLabel(item.clients, live)
            : item.clients_raw || "";
          return (
            <li key={item.id} className="log-card">
              <div className="meta">
                <span>{formatDate(item.published_at)}</span>
                <span className="pill">
                  {item.regulator_raw || item.regulators?.join(", ") || item.regulator}
                </span>
                {item.reference && <span className="ref">{item.reference}</span>}
                {item.board_flag && <span className="pill draft">Board</span>}
              </div>
              <h3>
                <ItemLink url={item.url}>{item.title}</ItemLink>
              </h3>
              {item.summary && <Clamped text={item.summary} />}
              <dl className="log-card-facts">
                {clients && (
                  <>
                    <dt>Clients</dt>
                    <dd>{clients}</dd>
                  </>
                )}
                {(item.tags ?? []).length > 0 && (
                  <>
                    <dt>Tags</dt>
                    <dd>{(item.tags ?? []).join(", ")}</dd>
                  </>
                )}
                {item.norwood_action && (
                  <>
                    <dt>Norwood</dt>
                    <dd>{item.norwood_action}</dd>
                  </>
                )}
              </dl>
              <button type="button" className="log-card-steps" onClick={() => onActions(item)}>
                <span className="label">Next steps</span>
                {item.next_steps ? (
                  <span className={item.next_steps_status === "complete" ? "done" : ""}>
                    {item.next_steps}
                  </span>
                ) : (
                  <span className="muted">Add…</span>
                )}
              </button>
              <div className="log-card-do">
                <button type="button" onClick={() => onEdit(item)}>
                  Edit
                </button>
              </div>
            </li>
          );
        })}
      </ul>
      {shown < items.length && (
        <button type="button" className="show-more" onClick={() => setShown((n) => n + PAGE)}>
          Show {Math.min(PAGE, items.length - shown)} more of {items.length - shown}
        </button>
      )}
    </>
  );
}

/** Her summaries run past 1,400 characters; four lines on a card, the rest on a tap. */
function Clamped({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  const long = text.length > 220;
  return (
    <div className="log-card-summary">
      <p className={open || !long ? "" : "clamped"}>{text}</p>
      {long && (
        <button type="button" className="link" onClick={() => setOpen(!open)}>
          {open ? "Show less" : "Read all"}
        </button>
      )}
    </div>
  );
}

interface SheetProps {
  item: Item;
  clients: Client[];
  live: string[];
  onClose: () => void;
  onSave: (patch: ItemPatch) => Promise<void>;
}

const listToText = (values: string[] | undefined) => (values ?? []).join(", ");
const textToList = (text: string) =>
  text
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);

export function LogItemSheet({ item, clients, live, onClose, onSave }: SheetProps) {
  const [summary, setSummary] = useState(item.summary ?? "");
  const [keyPoints, setKeyPoints] = useState(item.key_points ?? "");
  const [picked, setPicked] = useState<string[]>(item.clients ?? []);
  const [tags, setTags] = useState(listToText(item.tags));
  const [norwood, setNorwood] = useState(item.norwood_action ?? "");
  const [board, setBoard] = useState(Boolean(item.board_flag));
  const [status, setStatus] = useState(item.status);
  const [saving, setSaving] = useState(false);

  // Every chip she can tick, plus any former client already on the item — shown ticked, or
  // saving this sheet would silently drop her history with them (the grid picker's rule).
  const names = useMemo(() => {
    const offered = clientTags(clients);
    return [...offered, ...(item.clients ?? []).filter((name) => !offered.includes(name))];
  }, [clients, item.clients]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => event.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  /** Only what changed goes up, so the audit line says what she edited and nothing else. */
  const patch = (): ItemPatch => {
    const out: ItemPatch = {};
    if (summary !== (item.summary ?? "")) out.summary = summary;
    if (keyPoints !== (item.key_points ?? "")) out.key_points = keyPoints;
    const before = [...(item.clients ?? [])].sort().join("\n");
    if ([...picked].sort().join("\n") !== before) out.clients = picked;
    if (tags !== listToText(item.tags)) out.tags = textToList(tags);
    if (norwood !== (item.norwood_action ?? "")) out.norwood_action = norwood;
    if (board !== Boolean(item.board_flag)) out.board_flag = board;
    if (status !== item.status) out.status = status;
    return out;
  };

  const changes = patch();
  const dirty = Object.keys(changes).length > 0;

  const save = async () => {
    setSaving(true);
    try {
      await onSave(changes);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div
        className="sheet log-sheet"
        role="dialog"
        aria-label={`Edit ${item.title}`}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="sheet-head">
          <button type="button" className="link" onClick={onClose}>
            Cancel
          </button>
          <strong>Edit entry</strong>
          <button type="button" className="primary" disabled={!dirty || saving} onClick={save}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
        <div className="sheet-body">
          <p className="small muted">{item.title}</p>

          <label>
            Summary
            <textarea rows={6} value={summary} maxLength={5000} onChange={(e) => setSummary(e.target.value)} />
          </label>
          {item.ai_summary && item.ai_summary !== summary && (
            <p className="small">
              <span className="ai-badge">AI</span>A drafted summary is waiting.{" "}
              <button type="button" className="link" onClick={() => setSummary(item.ai_summary ?? "")}>
                use it
              </button>
            </p>
          )}

          <label>
            Key points
            <textarea rows={5} value={keyPoints} maxLength={5000} onChange={(e) => setKeyPoints(e.target.value)} />
          </label>

          <fieldset className="sheet-clients">
            <legend>Clients</legend>
            <div className="tags">
              {names.map((name) => {
                const on =
                  name === ALL_CLIENTS_TAG ? coversLive(picked, live) : picked.includes(name);
                return (
                  <button
                    key={name}
                    type="button"
                    className={on ? "chip on" : "chip"}
                    aria-pressed={on}
                    onClick={() => setPicked(toggleClient(picked, name, live))}
                  >
                    {name}
                    {name !== ALL_CLIENTS_TAG && !live.includes(name) ? " (former)" : ""}
                  </button>
                );
              })}
            </div>
          </fieldset>

          <label>
            Tags <span className="small">(separate with commas)</span>
            <input value={tags} onChange={(e) => setTags(e.target.value)} autoCapitalize="words" />
          </label>

          <div className="sheet-pair">
            <label>
              Norwood action
              <select value={norwood} onChange={(e) => setNorwood(e.target.value)}>
                {NORWOOD_ACTIONS.map((value) => (
                  <option key={value} value={value}>
                    {value || "—"}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Status
              <select value={status} onChange={(e) => setStatus(e.target.value as Item["status"])}>
                {LOG_STATUSES.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <label className="inline sheet-check">
            <input type="checkbox" checked={board} onChange={(e) => setBoard(e.target.checked)} />
            Include in the board paper
          </label>
        </div>
      </div>
    </div>
  );
}
